<template>
    <div class="marketing-consent-overview">
        <div class="marketing-consent-overview-container">
            <h3 class="marketing-consent-overview-title typographies-heading-h3">
                {{ labels.title }}
            </h3>

            <div class="marketing-consent-overview__consent">
                <h4 class="marketing-consent-overview__consent-title typographies-heading-h4">
                    {{ labels.consent.title }}
                </h4>

                <p
                    class="marketing-consent-overview__consent-information typographies-paragraph-medium-regular"
                    v-html="labels.consent.information"
                />

                <CompassTooltip
                    :content="labels.consent.switchButtons.tooltip"
                    :disabled="permissions.can_edit_rgpd_consents"
                    placement="top-start"
                >
                    <CompassSwitch
                        v-model="consent.marketing.email"
                        :label="labels.consent.switchButtons.email.label"
                        :disabled="!permissions.can_edit_rgpd_consents"
                        orientation="left"
                        class="marketing-consent-overview__consent-switch"
                        @change="updateConsent"
                    />
                </CompassTooltip>

                <CompassTooltip
                    :content="labels.consent.switchButtons.tooltip"
                    :disabled="permissions.can_edit_rgpd_consents"
                    placement="top-start"
                >
                    <CompassSwitch
                        v-model="consent.marketing.sms"
                        :label="labels.consent.switchButtons.sms.label"
                        :disabled="!permissions.can_edit_rgpd_consents"
                        orientation="left"
                        class="marketing-consent-overview__consent-switch"
                        @change="updateConsent"
                    />
                </CompassTooltip>
            </div>

            <div class="marketing-consent-overview__logs">
                <h4 class="marketing-consent-overview__logs-title typographies-heading-h4">
                    {{ labels.logs.title }}
                </h4>

                <p
                    class="marketing-consent-overview__logs-information typographies-paragraph-medium-regular"
                    v-html="labels.logs.information"
                />

                <CompassTooltip
                    :content="labels.logs.buttons.tooltip"
                    :disabled="permissions.can_download_treatments_list"
                    placement="top-start"
                >
                    <CompassButton
                        :label="labels.logs.buttons.download.label"
                        :disabled="!permissions.can_download_treatments_list"
                        type="secondary"
                        leftIcon="download"
                        class="marketing-consent-overview__logs-button"
                        @click="sendConsentLogsRequest"
                    />
                </CompassTooltip>
            </div>
        </div>
    </div>
</template>

<script>
import marketingService from 'api/marketing/marketingService.js';
import { debounce } from 'lodash';
export default {
    data() {
        return {
            consent: {
                marketing: {
                    email: false,
                    sms: false,
                    phone: false,
                    postal_mail: false,
                    custom: false,
                },
            },
            labels: {
                title: this.$t.__('marketing.consent.MarketingConsentOverview.title'),
                consent: {
                    title: this.$t.__('marketing.consent.MarketingConsentOverview.consent_title'),
                    information: this.$t.__(
                        'marketing.consent.MarketingConsentOverview.consent_information',
                        { br: '</br>' },
                    ),
                    switchButtons: {
                        email: {
                            label: this.$t.__(
                                'marketing.consent.MarketingConsentOverview.consent_email_label',
                            ),
                        },
                        sms: {
                            label: this.$t.__(
                                'marketing.consent.MarketingConsentOverview.consent_sms_label',
                            ),
                        },
                        tooltip: this.$t.__(
                            'marketing.consent.MarketingConsentOverview.consent_tooltip',
                        ),
                    },
                },
                logs: {
                    title: this.$t.__('marketing.consent.MarketingConsentOverview.logs_title'),
                    information: this.$t.__(
                        'marketing.consent.MarketingConsentOverview.logs_information',
                        { br: '</br>' },
                    ),
                    buttons: {
                        download: {
                            label: this.$t.__(
                                'marketing.consent.MarketingConsentOverview.logs_download_label',
                            ),
                        },
                        tooltip: this.$t.__(
                            'marketing.consent.MarketingConsentOverview.logs_download_tooltip',
                        ),
                    },
                },
            },
        };
    },
    computed: {
        identity() {
            return this.$store.getters.identity;
        },
        campaigns() {
            return this.$store.getters.sellsyInformations.campaigns;
        },
        isCompany() {
            return this.identity.hasOwnProperty('isCorporation') && this.identity.isCorporation;
        },
        isIndividual() {
            return this.identity.hasOwnProperty('isCorporation') && !this.identity.isCorporation;
        },
        isContact() {
            return (
                !this.identity.hasOwnProperty('isCorporation') && this.identity.type === 'contact'
            );
        },
        permissions() {
            return this.$store.getters.permissions;
        },
    },
    beforeMount() {
        this.consent.marketing.email = this.campaigns.includes('email');
        this.consent.marketing.sms = this.campaigns.includes('sms');
    },
    methods: {
        updateConsent: debounce(async function () {
            try {
                if (this.isCompany) {
                    await marketingService.updateCompanyConsentSettings(
                        this.identity.id,
                        this.consent,
                    );
                } else if (this.isIndividual) {
                    await marketingService.updateIndividualConsentSettings(
                        this.identity.id,
                        this.consent,
                    );
                } else if (this.isContact) {
                    await marketingService.updateContactConsentSettings(
                        this.identity.id,
                        this.consent,
                    );
                }
            } catch {
                this.pushApiError();
            }
        }, 500),
        async sendConsentLogsRequest() {
            try {
                if (this.isCompany) {
                    await marketingService.sendCompanyConsentLogsExportRequest(this.identity.id);
                } else if (this.isIndividual) {
                    await marketingService.sendIndividualConsentLogsExportRequest(this.identity.id);
                } else if (this.isContact) {
                    await marketingService.sendContactConsentLogsExportRequest(this.identity.id);
                }

                this.pushConsentLogsNotif();
            } catch {
                this.pushApiError();
            }
        },
        pushApiError() {
            this.$pushToast({
                type: 'error',
                icon: true,
                text: this.$t.__('common.labels.error_retry'),
            });
        },
        pushConsentLogsNotif() {
            this.$pushNotification({
                text: this.$t.__(
                    'marketing.consent.MarketingConsentOverview.logs_download_in_progress',
                ),
                isSellsy: true,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.marketing-consent-overview {
    width: 100%;
    margin-top: var(--spacing-3);
    padding-bottom: var(--spacing-5);

    &-container {
        display: flex;
        flex-direction: column;
        padding: var(--spacing-3);
        border-radius: var(--spacing-0);
        border: 1px solid var(--colors-common-border-default, #ccccd1);
        background-color: var(--colors-common-background-default, #fff);
    }

    &-title {
        margin: 0 0 var(--spacing-4);
    }

    &__consent,
    &__logs {
        &-title {
            margin: 0 0 var(--spacing-1);
        }
        &-information {
            margin: 0 0 var(--spacing-2);
        }
    }

    &__consent {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: var(--spacing-3);

        &-switch {
            margin-bottom: var(--spacing-2);
        }
    }

    &__logs {
        position: relative;
        padding-top: var(--spacing-3);

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background-color: var(--colors-common-border-default, #ccccd1);
        }

        &-button {
            margin-bottom: var(--spacing-2);
        }
    }
}
</style>
